import {ChemicalFormatType} from 'chemistry/ChemistryConstants';
import {MarvinChemicalFormat, MOLCONVERT_URL} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as net from 'util/Net';

import {MoleculeGraphQL} from '../../../graphql-fragments/chemistry/__generated/MoleculeGraphQL';
import {UpdateMoleculeVersionResponseGraphQL} from '../../../graphql-fragments/chemistry/__generated/UpdateMoleculeVersionResponseGraphQL';

export function resetToVersion(
  moleculeId: string,
  moleculeVersionId: string,
  expectedCurrentVersionId: string
): Promise<{molecule: UpdateMoleculeVersionResponseGraphQL}> {
  return net.postJSONPromise({
    url: `/1/api/molecules/${moleculeId}/actions/reset-version`,
    data: {moleculeVersionId, expectedCurrentVersionId},
  });
}

export function createMolecule(
  name: string,
  folderId: string,
  structureFormat: ChemicalFormatType,
  chemicalStructure: string,
  schemaId: string | null,
  studyIds?: Array<string>
): Promise<MoleculeGraphQL> {
  return net.postJSONPromise({
    url: '/1/api/molecules',
    data: {
      name,
      folderId,
      schemaId,
      chemicalStructureInput: {structureFormat, value: chemicalStructure},
      studyIds,
    },
  });
}

export interface CreateMoleculeWithParentLinkParams {
  name: string;
  folderId?: string;
  schemaId: string;
  chemicalStructureInput: {structureFormat: ChemicalFormatType; value: string};
  schemaFields?: Record<string, unknown>;
  studyIds?: Array<string>;
  /** When set with namingStrategy, the created molecule (and auto-created parent, if any) is registered. */
  registryId?: string;
  namingStrategy?: RegistryLabelingStrategy;
}

export interface CreatedParentMolecule {
  id: string;
  name: string;
  canonicalSmiles: string;
}

export interface CreateMoleculeWithParentLinkResponse extends MoleculeGraphQL {
  parentMolecule?: CreatedParentMolecule;
}

export function createMoleculeWithParentLink(
  params: CreateMoleculeWithParentLinkParams
): Promise<CreateMoleculeWithParentLinkResponse> {
  return net.postJSONPromise({
    url: '/1/api/molecules:create-with-parent-link',
    data: params,
  });
}

export function updateChemicalStructure(
  moleculeId: string,
  expectedCurrentVersionId: string,
  structureFormat: ChemicalFormatType,
  chemicalStructure: string
): Promise<MoleculeGraphQL> {
  return net.postJSONPromise({
    url: `/1/api/molecules/${moleculeId}/actions/update-chemical-structure`,
    data: {chemicalStructureInput: {structureFormat, value: chemicalStructure}, expectedCurrentVersionId},
  });
}

export function stripSalts(
  structureFormat: ChemicalFormatType,
  chemicalStructure: string
): Promise<{chemicalStructure: string; salts: Array<string>}> {
  return net.postJSONPromise({
    url: '/1/api/molecules/strip-salts',
    data: {structureFormat, value: chemicalStructure},
  });
}

// Either structure or binaryStructure will be defined.
export type MolConvertOutput = {format: string} & (
  | {binaryStructure?: undefined; structure: string}
  | {binaryStructure: string; structure?: undefined}
);

export interface ParsedCounterpart {
  id: string;
  name: string;
  type: string;
  ratioNumerator: number;
  ratioDenominator: number;
}

export interface ExistingParent {
  id: string;
  name: string;
}

export interface ParseCounterpartsResponse {
  baseStructure: {canonicalCxSmiles: string; molfile: string};
  counterparts: Array<ParsedCounterpart>;
  existingParents: Array<ExistingParent>;
}

export function parseCounterparts(
  structureFormat: ChemicalFormatType,
  value: string,
  schemaId?: string
): Promise<ParseCounterpartsResponse> {
  return net.postJSONPromise({
    url: '/1/api/molecules:parse-counterparts',
    data: {structureFormat, value, ...(schemaId != null ? {schemaId} : {})},
  });
}

export function molConvert(
  inputFormat: MarvinChemicalFormat | string | null,
  saveFormat: MarvinChemicalFormat,
  sketcherValue: string
): Promise<MolConvertOutput> {
  if (inputFormat === MarvinChemicalFormat.CDX) {
    inputFormat = 'base64:cdx';
  }
  return net.postJSONPromise({
    url: MOLCONVERT_URL,
    data: {
      inputFormat,
      parameters: saveFormat,
      structure: sketcherValue,
    },
  });
}
